import * as React from "react";

import { Button } from "../button";
import { ChevronLeft, ChevronRight } from "../icons";
import { cn } from "../utils";

export interface DataPagerProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Total row count, shown after the page label when provided. */
  total?: number;
  unitLabel?: string;
  /**
   * When true, apply the in-Card gutter (px-6) + bottom pad so the strip lines
   * up with a table wrapped in a `<Card>`. Omit for a flush, edge-aligned strip.
   */
  wrapped?: boolean;
  /**
   * Show the strip even when there is only one page (disabled prev/next).
   * Empty tables (`totalPages < 1`) still hide. Default hides on a single page.
   */
  alwaysShow?: boolean;
  className?: string;
}

/**
 * The single in-table prev/next pager strip, shared by BOTH the interactive
 * `DataTable` engine and the server-paginated `DatabaseGrid` so every table
 * paginates identically — a flush footer band INSIDE the table's Card, never a
 * detached control floating below it.
 *
 * Self-guarding: renders `null` when there is one page or fewer, so a one-page
 * table shows no footer at all (callers don't need their own guard).
 */
export function DataPager({
  page,
  totalPages,
  onPageChange,
  total,
  unitLabel = "rows",
  wrapped,
  alwaysShow,
  className,
}: DataPagerProps) {
  if (totalPages < 1) return null;
  if (totalPages <= 1 && !alwaysShow) return null;

  const maxVisible = Math.min(5, totalPages);
  const startPage = Math.max(1, Math.min(page - 2, totalPages - maxVisible + 1));
  const numPages = Math.min(maxVisible, totalPages - startPage + 1);

  return (
    <div
      data-slot="data-pager"
      className={cn(
        "flex items-center justify-between border-t border-border pt-3 type-body",
        wrapped && "px-6 pb-3",
        className,
      )}
    >
      <span className="text-muted-foreground tabular-nums">
        Page {page} of {totalPages}
        {total != null ? ` · ${total.toLocaleString()} ${unitLabel}` : ""}
      </span>
      <div className="flex gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="size-4" aria-hidden />
        </Button>
        {Array.from({ length: numPages }, (_, i) => startPage + i).map((p) => (
          <Button
            type="button"
            key={p}
            variant={p === page ? "default" : "outline"}
            size="icon"
            aria-current={p === page ? "page" : undefined}
            onClick={() => onPageChange(p)}
            className="tabular-nums"
          >
            {p}
          </Button>
        ))}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          aria-label="Next page"
        >
          <ChevronRight className="size-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}
